import React, { useState, useCallback, useMemo } from "react";
import axios from "axios";
import { DATE_RANGE_OPTIONS, getDateRangePreset } from "./dateRangeUtils";
import WorkstationSelect from "./WorkstationSelect";
import {
  Box, Button, Chip, Fade, FormControl, Grid, IconButton,
  MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableContainer, Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon             from "@mui/icons-material/Refresh";
import SearchIcon              from "@mui/icons-material/Search";
import AssignmentTurnedInIcon  from "@mui/icons-material/AssignmentTurnedIn";
import { T } from "./adminTheme";
import {
  StyledTableRow, FilterField,
  TableStateWrapper, FilterableSortableTableHead, useSortableTable,
  applyColumnFilters, hasActiveColumnFilters,
} from "./shared";
import { formatDateTime } from "../../utils/dateFormatter";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLUMNS = [
  { id: "Employee_ID",   label: "Employee",  minWidth: 100 },
  { id: "machineCode",   label: "Machine",   minWidth: 100 },
  { id: "jobNo",         label: "Job No",    minWidth: 90  },
  { id: "customer",      label: "Customer",  minWidth: 120 },
  { id: "product",       label: "Product",   minWidth: 120 },
  { id: "mainOperation", label: "Operation", minWidth: 120 },
  { id: "InputQty",      label: "Input",     minWidth: 70, filterable: false },
  { id: "PassQty",       label: "Pass",      minWidth: 70, filterable: false },
  { id: "productionQty", label: "Prod",      minWidth: 70, filterable: false },
  { id: "wasteQty",      label: "Waste",     minWidth: 70, filterable: false },
  { id: "finishedGood",  label: "FG",        minWidth: 60  },
  { id: "status",        label: "Status",    minWidth: 90  },
  { id: "startDatetime", label: "Start",     minWidth: 160, filterable: false },
  { id: "endDatetime",   label: "End",       minWidth: 160, filterable: false },
];

export default function JobSummaryTab({ onSnack, endpoint, allocatedMachines = null }) {
  const theme  = useTheme();
  const t      = T(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  // Tab accent — red
  const ACCENT       = t.red;
  const ACCENT_ALPHA = t.redAlpha;
  const ACCENT_TEXT  = isDark ? "#f87171" : "#dc2626";

  const defaultRange = getDateRangePreset("this_week");
  const [datePreset, setDatePreset] = useState("this_week");
  const [rawRows,    setRawRows]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [filters, setFilters] = useState({
    machineCode: "",
    employeeId:  "",
    jobNo:       "",
    status:      "",
    startDate:   defaultRange.startDate,
    endDate:     defaultRange.endDate,
  });

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const searchEndpoint = endpoint || `${API_BASE_URL}/admin/jobsummary/search`;
      const res = await axios.post(searchEndpoint, {
        machineCode: filters.machineCode || null,
        employeeId:  filters.employeeId  || null,
        jobNo:       filters.jobNo       || null,
        status:      filters.status      || null,
        startDate:   filters.startDate   || null,
        endDate:     filters.endDate     || null,
      });
      setRawRows(res.data || []);
      setColumnFilters({});
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to load job summary.", "error");
    } finally {
      setLoading(false);
    }
  }, [endpoint, filters, onSnack]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(rawRows, columnFilters),
    [rawRows, columnFilters]
  );

  const { sortedRows: rows, sortField, sortDir, handleSort } =
    useSortableTable(columnFilteredRows, "startDatetime", "desc");

  const visibleRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) =>
      Object.values(row).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, tableSearch]);

  return (
    <Fade in>
      <Box>
        <Paper sx={{
          borderRadius: "14px",
          border: `0.5px solid ${t.border}`,
          background: t.s0,
          boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)" : "none",
          overflow: "hidden",
        }}>

          {/* ── Card header ─────────────────────────────────────────── */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: `0.5px solid ${t.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: isDark
              ? `linear-gradient(135deg, ${ACCENT_ALPHA} 0%, transparent 60%)`
              : "transparent",
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                background: ACCENT_ALPHA,
                border: `0.5px solid ${ACCENT}30`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: ACCENT,
                boxShadow: isDark ? `0 0 12px ${ACCENT}15` : "none",
              }}>
                <AssignmentTurnedInIcon sx={{ fontSize: 19 }} />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 400, color: t.heading, letterSpacing: "-0.01em", lineHeight: 1.2 }}>
                  Search and review job summary records
                </Typography>
              </Box>

              {rows.length > 0 && (
                <Box sx={{ px: 1.25, py: 0.25, borderRadius: "20px", background: isDark ? t.s3 : t.s2, border: `0.5px solid ${t.border}` }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.secondary }}>{rows.length}</Typography>
                </Box>
              )}
            </Box>

            <IconButton size="small" onClick={search}
              sx={{ border: `0.5px solid ${t.border}`, borderRadius: "8px", width: 32, height: 32, color: t.muted,
                "&:hover": { background: t.s2, borderColor: t.borderHov, color: t.secondary } }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* ── Filter section ──────────────────────────────────────── */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2.5 }}>
            <Box sx={{ p: 2, borderRadius: "12px", border: `0.5px solid ${t.border}`, background: t.s1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                  <WorkstationSelect
                    label="Workstation"
                    value={filters.machineCode}
                    allocatedMachines={allocatedMachines}
                    allLabel={allocatedMachines ? "All Allocated Machines" : "All Workstations"}
                    onChange={(value) => setFilters((f) => ({ ...f, machineCode: value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                  <FilterField label="Employee ID" value={filters.employeeId}
                    onChange={(e) => setFilters((f) => ({ ...f, employeeId: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                  <FilterField label="Job No" value={filters.jobNo}
                    onChange={(e) => setFilters((f) => ({ ...f, jobNo: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                  <FilterField label="Status" value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
                  <FormControl fullWidth size="small">
                    <Select value={datePreset} onChange={(e) => {
                      const val = e.target.value;
                      setDatePreset(val);
                      if (val !== "custom") {
                        const range = getDateRangePreset(val);
                        setFilters((f) => ({ ...f, startDate: range.startDate, endDate: range.endDate }));
                      }
                    }}>
                      {DATE_RANGE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
                  <FilterField label="Start Date" type="datetime-local"
                    value={filters.startDate ? filters.startDate.replace(" ", "T") : ""}
                    onChange={(e) => { setDatePreset("custom"); setFilters((f) => ({ ...f, startDate: e.target.value ? e.target.value.replace("T", " ") : "" })); }} />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3} xl={3}>
                  <FilterField label="End Date" type="datetime-local"
                    value={filters.endDate ? filters.endDate.replace(" ", "T") : ""}
                    onChange={(e) => { setDatePreset("custom"); setFilters((f) => ({ ...f, endDate: e.target.value ? e.target.value.replace("T", " ") : "" })); }} />
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, pt: 2, borderTop: `0.5px solid ${t.border}` }}>
                {rows.length > 0 ? (
                  <Chip size="small"
                    label={hasActiveColumnFilters(columnFilters) ? `${rows.length} of ${rawRows.length} jobs` : `${rows.length} job${rows.length !== 1 ? "s" : ""} found`}
                    sx={{ background: ACCENT_ALPHA, color: ACCENT_TEXT, border: `0.5px solid ${ACCENT}30`, fontWeight: 600, fontSize: 11 }}
                  />
                ) : <Box />}

                <Button variant="contained" onClick={search} disabled={loading}
                  startIcon={loading ? null : <SearchIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontWeight: 600, fontSize: 13, borderRadius: "8px",
                    background: isDark
                      ? `linear-gradient(135deg, #ef4444, #dc2626)`
                      : `linear-gradient(135deg, #ef4444, #dc2626)`,
                    color: "#fff",
                    boxShadow: isDark ? `0 2px 8px ${ACCENT}30` : `0 2px 6px ${ACCENT}40`,
                    "&:hover": { boxShadow: isDark ? `0 4px 14px ${ACCENT}40` : `0 4px 12px ${ACCENT}50`, transform: "translateY(-0.5px)" },
                    "&:disabled": { opacity: 0.6 },
                  }}>
                  {loading ? "Searching…" : "Search Jobs"}
                </Button>
              </Box>
            </Box>
          </Box>

          {rows.length > 0 && (
            <Box sx={{ px: 3, pb: 2 }}>
              <FilterField
                label="Filter loaded results"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />
              {tableSearch.trim() && (
                <Typography sx={{ mt: 0.75, fontSize: 11, color: t.muted }}>
                  Showing {visibleRows.length} of {rows.length} loaded record{rows.length !== 1 ? "s" : ""}.
                </Typography>
              )}
            </Box>
          )}

          {/* ── Table ───────────────────────────────────────────────── */}
          <TableStateWrapper
            loading={loading}
            empty={visibleRows.length === 0}
            emptyMessage="No job summary records found. Try adjusting your date range or filters."
            colSpan={COLUMNS.length} minHeight={320} skeletonRows={6}
          >
            <TableContainer>
              <Table size="small">
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={rawRows}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />
                <TableBody>
                  {visibleRows.map((row, idx) => {
                    const isFinished = String(row.status).toLowerCase() === "finished";
                    return (
                      <StyledTableRow key={`${row.Employee_ID}-${row.machineCode}-${row.jobNo}-${row.startDatetime}-${idx}`}>
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 700, color: t.heading }}>{row.Employee_ID}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.machineCode}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, color: t.blue }}>{row.jobNo}</TableCell>
                        <TableCell>{row.customer}</TableCell>
                        <TableCell>{row.product}</TableCell>
                        <TableCell>{row.mainOperation}</TableCell>
                        <TableCell sx={{ textAlign: "right", fontFamily: "monospace" }}>{row.InputQty ?? 0}</TableCell>
                        <TableCell sx={{ textAlign: "right", fontFamily: "monospace" }}>{row.PassQty ?? 0}</TableCell>
                        <TableCell sx={{ textAlign: "right", fontFamily: "monospace", color: isDark ? "#34d399" : t.green, fontWeight: 600 }}>
                          {row.productionQty ?? "—"}
                        </TableCell>
                        <TableCell sx={{ textAlign: "right", fontFamily: "monospace", color: row.wasteQty > 0 ? ACCENT_TEXT : t.muted }}>
                          {row.wasteQty ?? "—"}
                        </TableCell>
                        <TableCell>{row.finishedGood || "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status || "—"}
                            color={isFinished ? "success" : "default"}
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.startDatetime)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(row.endDatetime)}</TableCell>
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TableStateWrapper>
        </Paper>
      </Box>
    </Fade>
  );
}